import axios from 'axios';

/**
 * Generic Lambda function caller
 * Works with any Lambda endpoint and payload
 * 
 * @param {string} endpoint - Lambda endpoint URL
 * @param {object} payload - Request payload
 * @returns {Promise} Response from Lambda
 */
export async function callLambdaFunction(endpoint, payload) {
  // Log the endpoint being called for environment debugging
  console.log('[aiClient] Calling Lambda endpoint:', endpoint);
  console.log('[aiClient] Environment:', import.meta.env?.MODE, '| Origin:', window?.location?.origin);

  try {
    const response = await axios?.post(endpoint, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
      // Increase timeout to 60 seconds for production environments
      timeout: 60000,
      // Prevent axios from auto-transforming response — we handle it manually
      transformResponse: [(data) => data],
    });

    // Log raw response for debugging environment differences
    const rawData = response?.data;
    console.log('[aiClient] Raw response type:', typeof rawData);
    console.log('[aiClient] Raw response (first 300 chars):', String(rawData)?.slice(0, 300));

    // Handle both pre-parsed objects and raw JSON strings
    // Production environments may return a string if Content-Type header is missing/wrong
    let parsed;
    if (typeof rawData === 'string') {
      try {
        parsed = JSON.parse(rawData);
      } catch (parseErr) {
        // Attempt to extract JSON from a possibly fenced or prefixed response
        const extracted = extractJsonFromString(rawData);
        if (extracted) {
          parsed = extracted;
        } else {
          console.error('[aiClient] Failed to parse response string:', rawData?.slice(0, 500));
          throw new Error('Invalid JSON response from Lambda');
        }
      }
    } else if (typeof rawData === 'object' && rawData !== null) {
      parsed = rawData;
    } else {
      throw new Error('Unexpected response type from Lambda: ' + typeof rawData);
    }

    // Check if Lambda returned an error payload
    if (parsed?.error) {
      console.error('[aiClient] Lambda returned error payload:', parsed);
      throw new Error(parsed.error);
    }

    return parsed;
  } catch (error) {
    if (error?.response?.data) {
      let errData = error?.response?.data;
      if (typeof errData === 'string') {
        try { errData = JSON.parse(errData); } catch (_) {}
      }
      if (errData?.error) {
        console.error('[aiClient] Lambda Function Error:', {
          error: errData?.error,
          details: errData?.details,
        });
        throw new Error(errData.error);
      }
    }
    console.error('[aiClient] Lambda function error:', error?.message || error);
    throw error;
  }
}

/**
 * Extracts a JSON object from a string that may contain markdown fences,
 * preamble text, or other non-JSON content.
 * Strips ```json ... ``` fences and finds the first { to last } block.
 */
function extractJsonFromString(str) {
  if (!str) return null;
  try {
    // Strip markdown code fences
    let cleaned = str?.replace(/```json\s*/gi, '')?.replace(/```\s*/g, '')?.trim();

    // Find the first '{' and last '}' to extract the JSON object
    const start = cleaned?.indexOf('{');
    const end = cleaned?.lastIndexOf('}');
    if (start !== -1 && end !== -1 && end > start) {
      const jsonStr = cleaned?.slice(start, end + 1);
      return JSON.parse(jsonStr);
    }
  } catch (e) {
    console.error('[aiClient] extractJsonFromString failed:', e?.message);
  }
  return null;
}